import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getModelOptions, getQuota, ModelOption, QuotaInfo } from '../services/api';
import { useAuth } from './AuthContext';
import { setAuthToken } from '../services/api';

const SELECTED_MODEL_KEY = 'selectedModel';

// Default model option to use when API fails
const DEFAULT_MODEL: ModelOption = {
  provider: "azure_openai",
  model: "gpt-4.1-mini",
  apiVersion: "2025-01-01-preview",
  description: "Default model for general use."
};

interface ModelContextType {
  modelOptions: ModelOption[];
  selectedModelIndex: number;
  setSelectedModelIndex: (index: number) => void;
  isLoading: boolean;
  error: string | null;
  refreshModels: () => void;
  quota: QuotaInfo | null;
  refreshQuota: () => void;
}

const ModelContext = createContext<ModelContextType | null>(null);

const getModelIdentifier = (model: ModelOption): string => {
  return `${model.provider}|${model.model}|${model.apiVersion}`;
};

const loadPersistedModel = (): string | null => {
  try {
    return localStorage.getItem(SELECTED_MODEL_KEY);
  } catch (error) {
    console.error('Failed to load persisted model:', error);
    return null;
  }
};

const savePersistedModel = (model: ModelOption): void => {
  try {
    localStorage.setItem(SELECTED_MODEL_KEY, getModelIdentifier(model));
  } catch (error) {
    console.error('Failed to save persisted model:', error);
  }
};

export const ModelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, getAccessToken } = useAuth();
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([DEFAULT_MODEL]);
  const [selectedModelIndex, setSelectedModelIndex] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const [quota, setQuota] = useState<QuotaInfo | null>(null);

  // Fetch models only once when user is available
  useEffect(() => {
    // Skip if no user or already fetched
    if (!user || hasFetched || isLoading) {
      return;
    }

    const fetchModels = async () => {
      setIsLoading(true);
      setHasFetched(true);

      try {
        const token = await getAccessToken();
        setAuthToken(token || '');

        const [options, quotaInfo] = await Promise.all([
          getModelOptions(),
          getQuota().catch(() => null),
        ]);

        if (options && options.length > 0) {
          setModelOptions(options);

          const firstUsableIndex = options.findIndex(m => m.can_use !== false);
          const persistedModelId = loadPersistedModel();
          let indexToSelect = firstUsableIndex !== -1 ? firstUsableIndex : 0;

          if (persistedModelId) {
            const matchingIndex = options.findIndex(
              model => getModelIdentifier(model) === persistedModelId && model.can_use !== false
            );
            if (matchingIndex !== -1) {
              indexToSelect = matchingIndex;
            }
          }

          setSelectedModelIndex(indexToSelect);
          setError(null);
        } else {
          setModelOptions([DEFAULT_MODEL]);
          setError("No models available from API");
        }

        setQuota(quotaInfo);
      } catch (error) {
        console.error("Failed to load models:", error);
        setModelOptions([DEFAULT_MODEL]);
        setError("Failed to load models");
      } finally {
        setIsLoading(false);
      }
    };

    fetchModels();
  }, [user, getAccessToken, hasFetched, isLoading]);
  
  // Function to manually refresh quota only (called after a successful chat message)
  const refreshQuota = useCallback(async () => {
    if (!user) return;
    try {
      const quotaInfo = await getQuota();
      setQuota(quotaInfo);
    } catch (error) {
      console.error("Failed to refresh quota:", error);
    }
  }, [user]);

  // Function to manually refresh models (and quota)
  const refreshModels = async () => {
    if (user && !isLoading) {
      setIsLoading(true);
      try {
        const token = await getAccessToken();
        setAuthToken(token || '');

        const [options, quotaInfo] = await Promise.all([
          getModelOptions(),
          getQuota().catch(() => null),
        ]);

        if (options && options.length > 0) {
          setModelOptions(options);

          const firstUsableIndex = options.findIndex(m => m.can_use !== false);
          const persistedModelId = loadPersistedModel();
          let indexToSelect = firstUsableIndex !== -1 ? firstUsableIndex : 0;

          if (persistedModelId) {
            const matchingIndex = options.findIndex(
              model => getModelIdentifier(model) === persistedModelId && model.can_use !== false
            );
            if (matchingIndex !== -1) {
              indexToSelect = matchingIndex;
            }
          }

          setSelectedModelIndex(indexToSelect);
          setError(null);
        } else {
          setModelOptions([DEFAULT_MODEL]);
          setError("No models available from API");
        }

        setQuota(quotaInfo);
      } catch (error) {
        console.error("Failed to refresh models:", error);
        setModelOptions([DEFAULT_MODEL]);
        setError("Failed to load models");
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSetSelectedModelIndex = useCallback((index: number) => {
    if (index >= 0 && index < modelOptions.length && modelOptions[index]?.can_use !== false) {
      setSelectedModelIndex(index);
      savePersistedModel(modelOptions[index]);
    }
  }, [modelOptions]);

  const value = {
    modelOptions,
    selectedModelIndex,
    setSelectedModelIndex: handleSetSelectedModelIndex,
    isLoading,
    error,
    refreshModels,
    quota,
    refreshQuota,
  };
  
  return (
    <ModelContext.Provider value={value}>
      {children}
    </ModelContext.Provider>
  );
};

export const useModels = (): ModelContextType => {
  const context = useContext(ModelContext);
  if (!context) {
    throw new Error('useModels must be used within a ModelProvider');
  }
  return context;
};